import { useCallback, useState } from 'react';
import api, { useLazyGetTradesByMarketQuery } from '@/store/api';
import { useAppDispatch } from '@/store';
import {
	isCategoryModalShown,
	markCategoryModalShown,
	markUserHasTrades,
} from '../popups/use-check-user-trades.hook';
import { useSocketRoom } from '../realtime/use-socket-room.hook';
import { Trade } from '../../api';
import { handleLeaderboardSocketUpdate, updateMarkets } from '@/utils';
import {
	MARKET_DELETED,
	MATCH_UPDATED,
	TRADE_CREATED,
	MARKET_UPDATED,
	MARKET_BULK_UPDATED,
	JOIN_MATCHES_ROOM,
	ON_MATCHES_CHANGE,
	LEAVE_MATCHES_ROOM,
	JOIN_EVENT_LOBBIES_ROOM,
	ON_EVENT_LOBBIES,
	LEAVE_EVENT_LOBBIES_ROOM,
	JOIN_COMMON_MARKETS_ROOM,
	ON_MARKETS_CHANGE,
	LEAVE_COMMON_MARKETS_ROOM,
	JOIN_MARKETS_TRADES_ROOM,
	ON_MARKETS_TRADES,
	LEAVE_MARKETS_TRADES_ROOM,
	SLATE_SCOPE_UPDATED,
} from '@sportspredict/shared';

interface UseLobbyRealtimeSyncProps {
	socket: any;
	eventId: string | undefined;
	lobbyId: string | undefined;
	slatesData: any[];
	setSlatesData: (updater: (prev: any[]) => any[]) => void;
	activeTab: string;
	isOverallLeaderboard: boolean;
	selectedMatches: any[];
	eventResponse: any;
	markets: any[];
	setMarkets: (updater: (prev: any[]) => any[]) => void;
	isAdmin: boolean;
	isCrowdLockMode: boolean;
	scheduleRefetchLeaderboard: (...args: any[]) => void;
	scheduleRefetchUserLeaderboardRank: (...args: any[]) => void;
	setShouldRefetchLeaderboard: (value: boolean) => void;
	mergeLobbyResponse: (nextLobby: any) => void;
	currentEventListener: (socketData: any) => void;
	userTrades: any[];
	setUserTrades: (updater: any) => void;
	user: any;
	currentMarketId: any;
	setShowForcedEntryPopup: (value: boolean) => void;
	hasEverTradedRef: any;
	firstTradeShownRef: any;
	isUserHasTrades: boolean;
	FIRST_PREDICTION_MODAL_KEY: string | null;
	setShowFirstTradeModal: (value: boolean) => void;
	setHasEverTraded: (value: boolean) => void;
	refetchProfile: (...args: any[]) => void;
	firstCategoryTradeShownRef: any;
	tradesCount: number | undefined;
	setShowFirstTradeCategoryModal: (value: boolean) => void;
	refetchScopedLobbyData?: () => void | Promise<void>;
}

export function useLobbyRealtimeSync({
	socket,
	eventId,
	lobbyId,
	slatesData,
	setSlatesData,
	activeTab,
	isOverallLeaderboard,
	selectedMatches,
	eventResponse,
	markets,
	setMarkets,
	isAdmin,
	isCrowdLockMode,
	scheduleRefetchLeaderboard,
	scheduleRefetchUserLeaderboardRank,
	setShouldRefetchLeaderboard,
	mergeLobbyResponse,
	currentEventListener,
	userTrades,
	setUserTrades,
	user,
	currentMarketId,
	setShowForcedEntryPopup,
	hasEverTradedRef,
	firstTradeShownRef,
	isUserHasTrades,
	FIRST_PREDICTION_MODAL_KEY,
	setShowFirstTradeModal,
	setHasEverTraded,
	refetchProfile,
	firstCategoryTradeShownRef,
	tradesCount,
	setShowFirstTradeCategoryModal,
	refetchScopedLobbyData,
}: UseLobbyRealtimeSyncProps) {
	const dispatch = useAppDispatch();
	const [results, setMatchClocks] = useState(new Map());
	const [tradesByMarket, setTradesByMarket] = useState<Record<string, any>>({});

	const onMatchChangeHandler = useCallback(
		(sockData: any) => {
			const socketMatch = sockData?.data;

			if (!socketMatch) return;

			setSlatesData((prevSlates) => {
				const newSlates: any[] = [];

				prevSlates.forEach((slate: any) => {
					const newSlate = { ...slate };
					if (newSlate?.matches?.length) {
						const newMatches: any[] = [];

						newSlate.matches.forEach((match: any) => {
							if (match.id === socketMatch.id) {
								newMatches.push(socketMatch);
							} else {
								newMatches.push(match);
							}
						});
						newSlate.matches = newMatches;
					}
					newSlates.push(newSlate);
				});

				return newSlates;
			});
		},
		[setSlatesData]
	);

	useSocketRoom({
		socket,
		enabled: Boolean(socket && eventId && slatesData?.length),
		eventName: ON_MATCHES_CHANGE,
		handler: onMatchChangeHandler,
		join: () => socket.emit(JOIN_MATCHES_ROOM),
		leave: () => socket.emit(LEAVE_MATCHES_ROOM),
	});

	useSocketRoom({
		socket,
		enabled: Boolean(socket && eventId),
		eventName: ON_EVENT_LOBBIES,
		handler: currentEventListener,
		join: () => socket.emit(JOIN_EVENT_LOBBIES_ROOM),
		leave: () => socket.emit(LEAVE_EVENT_LOBBIES_ROOM),
	});

	const handleDataBroadcast = useCallback(
		async (sockData: any) => {
			if (!sockData?.tag || sockData?.eventId !== eventId || !lobbyId) {
				return;
			}

			if (sockData.tag === SLATE_SCOPE_UPDATED && sockData.slate?.id) {
				const updatedSlate = sockData.slate;
				const isVisible =
					updatedSlate.is_event_wide === true ||
					updatedSlate.lobby_ids?.some((id: string) => String(id) === String(lobbyId));
				const localSlate = slatesData.find(
					(slate: any) => String(slate.id) === String(updatedSlate.id)
				);
				const affectedMatchIds = new Set(
					(sockData.match_ids?.length
						? sockData.match_ids
						: localSlate?.matches?.map((match: any) => match.id) || []
					).map(String)
				);

				if (isVisible) {
					setSlatesData((prev) =>
						prev.map((slate: any) =>
							String(slate.id) === String(updatedSlate.id) ? { ...slate, ...updatedSlate } : slate
						)
					);
				} else {
					setSlatesData((prev) =>
						prev.filter((slate: any) => String(slate.id) !== String(updatedSlate.id))
					);
					setMarkets((prev) =>
						prev.filter(
							(market: any) =>
								market.match_id == null || !affectedMatchIds.has(String(market.match_id))
						)
					);
				}

				dispatch(api.util.invalidateTags([{ type: 'Slate', id: 'LIST' }]));
				Promise.resolve(refetchScopedLobbyData?.()).catch((error) => {
					console.error('Failed to refetch lobby data after slate scope update:', error);
				});
				return;
			}

			const marketId = sockData?.marketId || sockData?.market?.id;
			const isCurrentLobbyTrade =
				sockData.tag !== TRADE_CREATED || String(sockData.trade?.lobby_id) === String(lobbyId);
			const visibleMatchIds = new Set<string>(
				slatesData.flatMap(
					(slate: any) => slate.matches?.map((match: any) => String(match.id)) || []
				)
			);

			if (sockData?.lobby && String(sockData.lobby.id) === String(lobbyId)) {
				mergeLobbyResponse(sockData.lobby);
			}

			if (sockData.tag === MATCH_UPDATED) {
				const matchId = sockData?.match?.id;
				const openingDescription = sockData?.match?.opening_description ?? null;

				setSlatesData((prev) =>
					prev.map((slate: any) => ({
						...slate,
						matches: slate.matches.map((match: any) =>
							matchId
								? match.id === matchId
									? { ...match, opening_description: openingDescription }
									: match
								: { ...match, opening_description: null }
						),
					}))
				);
			}

			if (isCurrentLobbyTrade) {
				handleLeaderboardSocketUpdate(
					sockData,
					activeTab,
					isOverallLeaderboard,
					selectedMatches,
					eventResponse?.is_odds_locked,
					markets,
					scheduleRefetchLeaderboard,
					isAdmin || isCrowdLockMode ? undefined : scheduleRefetchUserLeaderboardRank,
					setShouldRefetchLeaderboard
				);
			}

			setMarkets((prev) => updateMarkets(prev, sockData, { lobbyId, visibleMatchIds }));

			if (!isCurrentLobbyTrade) return;

			if (sockData.result) {
				const {
					matchId,
					period,
					clock,
					homeScore,
					awayScore,
					league,
					isCompleted,
					winner,
					periods,
					gameScore,
				} = sockData.result;
				if (matchId) {
					setMatchClocks((prev) => {
						const next = new Map(prev);
						next.set(matchId, {
							period,
							clock,
							homeScore,
							awayScore,
							league,
							isCompleted,
							winner,
							periods,
							gameScore,
						});
						return next;
					});
				}
			}

			if (sockData.tag === TRADE_CREATED) {
				const trade = sockData.trade;

				if (user && trade.user_id === user?.id) {
					setUserTrades((prev: any[]) => [...prev, trade]);
					if (String(trade.market_id) === String(currentMarketId)) {
						setShowForcedEntryPopup(false);
					}
				}

				const marketTrades = tradesByMarket?.[marketId];
				if (marketTrades) {
					setTradesByMarket((prev) => ({
						...prev,
						[marketId]: [...marketTrades, trade],
					}));
				}
			}

			if (sockData.tag === MARKET_DELETED && marketId) {
				if (tradesByMarket?.[marketId]) {
					setTradesByMarket((prev) => {
						const copy = { ...prev };
						delete copy[marketId];
						return copy;
					});
				}

				const hasUserTrades = userTrades.some((t: any) => t.market_id === marketId);
				if (hasUserTrades) {
					setUserTrades((prev: any[]) => prev.filter((t) => t.market_id !== marketId));
				}
			}

			if (
				(sockData.tag === MARKET_UPDATED || sockData.tag === MARKET_BULK_UPDATED) &&
				!isAdmin &&
				user?.id &&
				eventId &&
				!isCrowdLockMode
			) {
				Trade.filter({ user_id: user.id, event_id: eventId })
					.then((trades: any[]) => {
						if (trades?.length) {
							const currentLobbyTrades = trades.filter((t) => t.lobby_id === lobbyId);
							setUserTrades(currentLobbyTrades);
						}
					})
					.catch((err: any) => {
						console.error('Failed to refetch user trades on market update:', err);
					});
			}

			if (
				sockData.tag === TRADE_CREATED &&
				sockData.trade?.user_id === user?.id &&
				!hasEverTradedRef.current &&
				!firstTradeShownRef.current
			) {
				let alreadyShown = isUserHasTrades;
				if (!alreadyShown && FIRST_PREDICTION_MODAL_KEY) {
					alreadyShown = localStorage.getItem(FIRST_PREDICTION_MODAL_KEY) === '1';
				}

				if (!alreadyShown) {
					firstTradeShownRef.current = true;
					setShowFirstTradeModal(true);
				} else {
					markUserHasTrades();
				}

				setHasEverTraded(true);
				hasEverTradedRef.current = true;

				if (FIRST_PREDICTION_MODAL_KEY) {
					localStorage.setItem(FIRST_PREDICTION_MODAL_KEY, '1');
				}

				refetchProfile?.();
			}
			if (sockData.tag === TRADE_CREATED && sockData.trade?.user_id === user?.id) {
				const categoryId = eventResponse?.category_id;
				const categoryAlreadyShown =
					categoryId && user?.id ? isCategoryModalShown(user.id, categoryId) : true;
				if (
					user?.id &&
					categoryId &&
					!categoryAlreadyShown &&
					!firstCategoryTradeShownRef.current.has(categoryId) &&
					tradesCount !== undefined &&
					tradesCount === 0
				) {
					firstCategoryTradeShownRef.current.add(categoryId);
					markCategoryModalShown(user.id, categoryId);
					setShowFirstTradeCategoryModal(true);
				}
			}
		},
		[
			eventId,
			lobbyId,
			mergeLobbyResponse,
			setSlatesData,
			activeTab,
			isOverallLeaderboard,
			selectedMatches,
			eventResponse,
			markets,
			scheduleRefetchLeaderboard,
			isAdmin,
			isCrowdLockMode,
			scheduleRefetchUserLeaderboardRank,
			setShouldRefetchLeaderboard,
			setMarkets,
			tradesByMarket,
			user,
			setUserTrades,
			currentMarketId,
			setShowForcedEntryPopup,
			userTrades,
			hasEverTradedRef,
			firstTradeShownRef,
			isUserHasTrades,
			FIRST_PREDICTION_MODAL_KEY,
			setShowFirstTradeModal,
			setHasEverTraded,
			refetchProfile,
			firstCategoryTradeShownRef,
			tradesCount,
			setShowFirstTradeCategoryModal,
			refetchScopedLobbyData,
			slatesData,
			dispatch,
		]
	);

	useSocketRoom({
		socket,
		enabled: Boolean(socket && eventId),
		eventName: ON_MARKETS_CHANGE,
		handler: handleDataBroadcast,
		join: () => socket.emit(JOIN_COMMON_MARKETS_ROOM),
		leave: () => socket.emit(LEAVE_COMMON_MARKETS_ROOM),
	});

	const [getTradesByMarket] = useLazyGetTradesByMarketQuery();

	const handleGetTradesByMarket = useCallback(
		async (marketId: any, options: { force?: boolean } = {}) => {
			const { force = false } = options;

			if (!force && tradesByMarket?.[marketId]) {
				return tradesByMarket[marketId];
			}

			try {
				const result = await getTradesByMarket({ id: marketId }).unwrap();

				setTradesByMarket((prev) => ({
					...prev,
					[marketId]: result,
				}));

				return result;
			} catch (error) {
				console.error('Error handleGetTradesByMarket', error);
				return tradesByMarket?.[marketId] ?? [];
			}
		},
		[getTradesByMarket, tradesByMarket]
	);

	useSocketRoom({
		socket,
		enabled: Boolean(socket && eventId),
		eventName: ON_MARKETS_TRADES,
		handler: (sockData: any) => {
			if (sockData?.tag !== TRADE_CREATED) return;
			if (sockData.eventId !== eventId) return;

			const { marketId, trade } = sockData;
			if (!trade || String(trade.lobby_id) !== String(lobbyId)) return;

			setTradesByMarket((prev) => {
				const prevTrades = prev[marketId] ?? [];

				if (prevTrades.some((t: any) => t?.id === trade?.id)) {
					return prev;
				}

				return {
					...prev,
					[marketId]: [trade, ...prevTrades],
				};
			});
		},
		join: () => socket.emit(JOIN_MARKETS_TRADES_ROOM, { eventId: String(eventId) }),
		leave: () => socket.emit(LEAVE_MARKETS_TRADES_ROOM, { eventId: String(eventId) }),
	});

	return {
		results,
		setMatchClocks,
		tradesByMarket,
		setTradesByMarket,
		handleGetTradesByMarket,
	};
}
